/**
 * Unified Environment Configuration
 * Only 3 environments: LOCAL, STAGING, PRODUCTION
 */

// Environment detection - simplified for your 2 environments
const getEnvironment = () => {
  // Check if running on localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'LOCAL';
  }
  
  // Everything else is production
  return 'PRODUCTION';
};

export const ENVIRONMENT = getEnvironment();

// Environment configurations
export const ENV_CONFIG = {
  LOCAL: {
    name: 'Local Development',
    api: {
      baseUrl: 'http://localhost:5002',  // Updated port to avoid conflicts
      zatcaUrl: 'http://localhost:5001'
    },
    zatca: {
      environment: 'NonProduction',  // Your requirement: Local with NonProduction ZATCA
      endpoints: 'developer-portal'
    },
    database: 'development',
    debug: true
  },
  
  PRODUCTION: {
    name: 'Production Environment',
    api: {
      baseUrl: 'https://api.eazyfatoora.com',
      zatcaUrl: 'https://zatca.eazyfatoora.com'
    },
    zatca: {
      environment: 'Production',  // Your requirement: Production with Production ZATCA
      endpoints: 'core'
    },
    database: 'production',
    debug: false
  }
};

// Current environment config
export const CURRENT_CONFIG = ENV_CONFIG[ENVIRONMENT];

// API configuration
export const API_CONFIG = {
  BASE_URL: CURRENT_CONFIG.api.baseUrl,
  ZATCA_URL: CURRENT_CONFIG.api.zatcaUrl,
  API_PATH: '/api',
  
  get API_URL() {
    return `${this.BASE_URL}${this.API_PATH}`;
  },
  
  get ZATCA_API_URL() {
    return `${this.ZATCA_URL}${this.API_PATH}`;
  },
  
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'X-Environment': ENVIRONMENT,
    'X-ZATCA-Environment': CURRENT_CONFIG.zatca.environment
  },
  
  TIMEOUT: 30000
};

// ZATCA Configuration
export const ZATCA_CONFIG = {
  ENVIRONMENT: CURRENT_CONFIG.zatca.environment,
  ENDPOINTS: CURRENT_CONFIG.zatca.endpoints,
  
  // Invoice types
  INVOICE_TYPES: {
    STANDARD_ONLY: '1000',
    SIMPLIFIED_ONLY: '0100', 
    BOTH: '1100'  // Recommended
  },
  
  // Test data for non-production
  TEST_DATA: {
    VAT_NUMBER: '399999999900003',
    OTP: '123345',
    SERIAL_FORMAT: '1-TST|2-TST|3-{GUID}'
  }
};

// Environment utilities - simplified for your 2 environments
export const ENV_UTILS = {
  isLocal: () => ENVIRONMENT === 'LOCAL',
  isProduction: () => ENVIRONMENT === 'PRODUCTION',
  isDevelopment: () => ENVIRONMENT === 'LOCAL',
  
  getZatcaEnvironment: () => CURRENT_CONFIG.zatca.environment,
  getApiUrl: (endpoint = '') => `${API_CONFIG.API_URL}${endpoint}`,
  getZatcaUrl: (endpoint = '') => `${API_CONFIG.ZATCA_API_URL}${endpoint}`,
  
  log: (message, data = null) => {
    if (CURRENT_CONFIG.debug) {
      console.log(`[${ENVIRONMENT}] ${message}`, data);
    }
  }
};

// Authentication configuration
export const AUTH_CONFIG = {
  // Key for storing the authentication token
  TOKEN_KEY: 'token',
  
  // Key for storing the user data
  USER_KEY: 'user',
  
  // Key for storing the tenant data
  TENANT_KEY: 'tenant',
  
  // Key for storing the session ID
  SESSION_KEY: 'zatca_session_id',
};

// Application configuration
export const APP_CONFIG = {
  // Application name
  APP_NAME: 'Eazy Fatoora',
  VERSION: '2.0.0',
  ENVIRONMENT: ENVIRONMENT,
  
  // Default currency
  DEFAULT_CURRENCY: 'SAR',
  
  // Default language
  DEFAULT_LANGUAGE: 'en',
  
  // Default date format
  DEFAULT_DATE_FORMAT: 'DD/MM/YYYY',
  
  // Default time format
  DEFAULT_TIME_FORMAT: 'HH:mm',
  
  // Default pagination settings
  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 10,
    OPTIONS: [5, 10, 25, 50, 100],
  },
  
  // Features by environment - simplified for your 2 environments
  FEATURES: {
    LOCAL: {
      devTools: true,
      mockData: false,  // You use real NonProduction ZATCA
      debugMode: true
    },
    PRODUCTION: {
      devTools: false,
      mockData: false,
      debugMode: false
    }
  }
};

export default {
  ENVIRONMENT,
  ENV_CONFIG,
  CURRENT_CONFIG,
  API_CONFIG,
  ZATCA_CONFIG,
  ENV_UTILS,
  APP_CONFIG,
  AUTH_CONFIG
};
